import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell } from "@/components/intel/ModuleShell";
import { IntelligenceCanvas } from "@/components/intel/IntelligenceCanvas";
import { loadGraph, type GraphEdge, type GraphNode } from "@/lib/intel/graph.functions";

export const Route = createFileRoute("/vault/$scanId/canvas")({
  head: () => ({ meta: [
    { title: "Intelligence Canvas · CARIO INTEL" },
    { name: "description", content: "Infinite visual investigation workspace for entities, evidence, documents, and notes." },
    { property: "og:title", content: "Intelligence Canvas · CARIO INTEL" },
    { property: "og:description", content: "Connect people, companies, infrastructure, files, screenshots, and notes in one persistent case canvas." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: CanvasPage,
});

function CanvasPage() {
  const { scanId } = Route.useParams();
  const load = useServerFn(loadGraph);
  const query = useQuery({ queryKey: ["graph", scanId], queryFn: () => load({ data: { scanRunId: scanId } }) });
  const nodes = useMemo<GraphNode[]>(() => query.data ? [...query.data.seed.nodes, ...query.data.nodes] : [], [query.data]);
  const edges = useMemo<GraphEdge[]>(() => query.data ? [...query.data.seed.edges, ...query.data.edges] : [], [query.data]);
  return <AppLayout><ModuleShell eyebrow="Investigation Workspace" title="Intelligence canvas" description="Drag entities, infrastructure, files and notes into an infinite workspace. Connect items visually; positions and links are saved to this case." meta={<Link to="/vault/$scanId" params={{ scanId }} className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs"><ArrowLeft className="h-3 w-3" /> Case</Link>}>
    {query.isLoading ? <div className="glass rounded-lg p-8 text-center text-sm text-muted-foreground">Loading canvas…</div> : <IntelligenceCanvas key={`${scanId}-${nodes.length}-${edges.length}`} scanRunId={scanId} graphNodes={nodes} graphEdges={edges} />}
  </ModuleShell></AppLayout>;
}